import { currentLocale, intlLocaleFor } from "./index";

/**
 * Numbers and clock times in the viewer's language and time zone, always
 * through `intlLocaleFor()` (never hand `bm` / `tet` to Intl). Absolute
 * times only — an operator needs "14:05", never "5 min ago".
 */

const cache = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat>();

function dtf(key: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
	const id = `${currentLocale()}|d|${key}`;
	let f = cache.get(id) as Intl.DateTimeFormat | undefined;
	if (!f) {
		f = new Intl.DateTimeFormat(intlLocaleFor(currentLocale()), opts);
		cache.set(id, f);
	}
	return f;
}

const toDate = (v: string | Date): Date => (typeof v === "string" ? new Date(v) : v);

/** `14:05` (or the locale's own clock). */
export const formatClock = (v: string | Date): string =>
	dtf("clock", { hour: "numeric", minute: "2-digit" }).format(toDate(v));

/** `2:05:37 PM` — an audit trail's exact instant (the full stamp with its zone goes in the tooltip). */
export const formatClockExact = (v: string | Date): string =>
	dtf("clockExact", { hour: "numeric", minute: "2-digit", second: "2-digit" }).format(toDate(v));

/** `Wed 1 Oct` — the day chip. */
export const formatDay = (v: string | Date): string =>
	dtf("day", { weekday: "short", day: "numeric", month: "short" }).format(toDate(v));

/** `1 Oct, 2:05 PM` — dense lists (matches GMS `formatShort`). */
export const formatShort = (v: string | Date): string =>
	dtf("short", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }).format(toDate(v));

/** Full stamp with zone, for tooltips. */
export const formatDateTime = (v: string | Date): string =>
	dtf("full", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
		timeZoneName: "short",
	}).format(toDate(v));

/** A plain integer in the viewer's digits (`1,234`). */
export function formatInteger(n: number): string {
	const id = `${currentLocale()}|n|int`;
	let f = cache.get(id) as Intl.NumberFormat | undefined;
	if (!f) {
		f = new Intl.NumberFormat(intlLocaleFor(currentLocale()), { maximumFractionDigits: 0 });
		cache.set(id, f);
	}
	return f.format(n);
}
